import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, MessageCircle, ArrowUpRight } from "lucide-react";

// Masked line reveal helper — wrap each word/line in overflow-hidden
const MaskedWord = ({ children, delay = 0 }) => (
    <span className="inline-block overflow-hidden align-bottom pb-[0.06em]">
        <motion.span
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.05, ease: [0.16, 1, 0.3, 1], delay }}
            className="inline-block"
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero({ content }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const logoY = useTransform(scrollYProgress, [0, 1], [0, -120]);
    const logoScale = useTransform(scrollYProgress, [0, 1], [1, 0.85]);
    const logoRotate = useTransform(scrollYProgress, [0, 1], [0, 6]);
    const bgY = useTransform(scrollYProgress, [0, 1], [0, 200]);

    return (
        <section
            ref={ref}
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden bg-[color:var(--bg-primary)] pt-28 md:pt-32"
        >
            {/* Grid + subtle blueprint bg */}
            <motion.div style={{ y: bgY }} className="absolute inset-0 grid-bg pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[color:var(--brand-blue)]/8 blur-3xl" />
                <div className="absolute bottom-0 -left-20 h-[300px] w-[300px] rounded-full bg-[color:var(--brand-blue)]/6 blur-3xl" />
            </div>

            <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
                {/* Top row: eyebrow + meta */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="flex items-center justify-between text-[11px] uppercase tracking-[0.25em] text-neutral-500 font-mono"
                >
                    <span>[ 01 — {content.hero_eyebrow} ]</span>
                    <span className="hidden md:inline">EST. — Licensed & Insured</span>
                </motion.div>

                {/* Massive kinetic headline + logo */}
                <div className="mt-8 md:mt-12 grid grid-cols-12 gap-6 items-start">
                    <div className="col-span-12 lg:col-span-8">
                        <h1 className="font-display font-black leading-[0.88] tracking-tighter text-neutral-900 text-[15vw] sm:text-[13vw] lg:text-[10.5vw] xl:text-[9.5rem]">
                            <div className="block">
                                <MaskedWord delay={0.35}>WE&nbsp;BUILD</MaskedWord>
                            </div>
                            <div className="block text-[color:var(--brand-blue)]">
                                <MaskedWord delay={0.55}>YOUR&nbsp;VISION.</MaskedWord>
                            </div>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.9, duration: 0.7 }}
                            className="mt-10 max-w-xl text-base md:text-lg text-neutral-600 leading-relaxed"
                            data-testid="hero-subtitle"
                        >
                            {content.hero_subtitle}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.05, duration: 0.6 }}
                            className="mt-10 flex flex-wrap items-center gap-4"
                        >
                            <a
                                href={`tel:${content.phone_raw}`}
                                data-testid="hero-call-btn"
                                className="btn-brand group inline-flex items-center gap-3 rounded-full bg-[color:var(--brand-blue)] px-7 py-4 text-sm md:text-base font-semibold text-white uppercase tracking-wider"
                            >
                                <Phone className="h-4 w-4" />
                                Free Estimate
                                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                            <a
                                href={`https://wa.me/${content.whatsapp}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="hero-whatsapp-btn"
                                className="group inline-flex items-center gap-2 rounded-full border border-neutral-900/20 bg-white/60 backdrop-blur-md px-6 py-4 text-sm md:text-base font-semibold text-neutral-900 hover:border-neutral-900 transition-colors"
                            >
                                <MessageCircle className="h-4 w-4" />
                                Text on WhatsApp
                            </a>
                        </motion.div>
                    </div>

                    {/* Dynamic Logo — floats with parallax + subtle 3D tilt on load */}
                    <motion.div
                        style={{ y: logoY, scale: logoScale, rotate: logoRotate }}
                        className="col-span-12 lg:col-span-4 lg:mt-6"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ delay: 0.15, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                            className="relative flex items-center justify-center"
                        >
                            <div className="absolute inset-0 blur-3xl bg-[color:var(--brand-blue)]/15 rounded-full" />
                            <motion.img
                                src={content.logo_url}
                                alt="Steven R. Construction LLC Logo"
                                data-testid="hero-logo"
                                className="relative w-full max-w-md lg:max-w-none drop-shadow-2xl select-none"
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                                draggable={false}
                            />
                        </motion.div>
                    </motion.div>
                </div>

                {/* Bottom meta rail */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.3, duration: 0.7 }}
                    className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 border-t border-neutral-900/10"
                >
                    {[
                        ["10+", "Years crafting homes"],
                        ["200+", "Projects delivered"],
                        ["100%", "Licensed & insured"],
                        ["24h", "Response window"],
                    ].map(([num, label]) => (
                        <div key={label} className="border-r last:border-r-0 border-neutral-900/10 py-6 md:py-8 pl-4 md:pl-6">
                            <div className="font-display text-3xl md:text-5xl font-bold tracking-tighter text-neutral-900">{num}</div>
                            <div className="mt-2 text-[10px] md:text-xs uppercase tracking-[0.2em] text-neutral-600 font-medium">{label}</div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
